/**
 * 放送地点マスタ画面の「入力エラーを分かりやすく・見つけやすくする」ための
 * 構造化バリデーション結果 (`ValidationIssue`) の型と、位置情報から
 * `data-testid` を解決するヘルパをまとめたモジュール。
 *
 * ## なぜ構造化するのか
 *
 * 従来のバリデーション (`validateContent` / `validate`) は
 * 「最初に見つかったエラーの日本語メッセージを 1 件だけ返して early return」
 * という実装だった。メッセージ自体は
 * `コンテンツ #2 (接続線遅延／乗換駅別) 接続線1 パターン3: ...` のように詳しいが、
 *
 *   1. 一度に 1 件しか分からない (直しても直しても次のエラーが出る)
 *   2. **該当項目が画面上で隠れている** ことが多い
 *      - コンテンツが 2 件以上だとアコーディオンで折りたたまれている
 *      - 接続線・方面はタブ表示で選択中の 1 件しか描画されない
 *   3. エラー表示はモーダルフッター (画面下) にあり、該当項目までの導線が無い
 *
 * という理由で「どこを直せばいいのか分からない」状態になっていた。
 *
 * そこで **「エラー文言」と「エラーの発生位置」を分離** し、位置情報をもとに
 *   - 折りたたみ / タブを自動で開く (`ValidationTarget` → `RevealTarget`)
 *   - 該当入力欄までスクロール + フォーカス + 赤枠 (`data-testid` で特定)
 *   - エラー件数をコンテンツカード / タブにバッジ表示
 * ができるようにする。
 *
 * ## 後方互換について
 *
 * `message` は **従来の文言と完全に同一** にすること。既存の Vitest / E2E は
 * 文言 (`/緯度/` など) で検証しているため、文言を変えると回帰する。
 * 既存 API (`validateContent` / `validate`) は「先頭 issue の message を返す
 * ラッパー」として維持されるので、呼び出し側は段階的に移行できる。
 */

/**
 * エラーが発生した場所を表す座標。
 *
 * すべて任意フィールドで、浅い階層のものだけが埋まる場合もある
 * (例: 「説明を入力してください」は `testId: 'description-input'` のみ)。
 */
export interface ValidationTarget {
  /**
   * `values.contents[]` の 0 始まり index。
   * 地点の基本情報 (説明 / 緯度 / 半径 等) のエラーでは undefined。
   */
  contentIndex?: number;
  /** 接続線遅延 (乗換駅別) の `properties.lines[]` の 0 始まり index。 */
  lineIndex?: number;
  /** 接続線ブロック内 `directions[]` の 0 始まり index。 */
  directionIndex?: number;
  /** 遅延放送パターン `delay_patterns[]` の 0 始まり index。 */
  patternIndex?: number;
  /** 任意テキストの放送条件 `conditions[]` の 0 始まり index。 */
  conditionIndex?: number;
  /**
   * 該当入力欄の `data-testid`。
   *
   * スクロール・フォーカス・赤枠 (`.is-invalid`) の付与対象を一意に決めるために使う。
   * 「1 件以上追加してください」のように**特定の入力欄が存在しない**エラーでは
   * 追加ボタン等の testid を入れるか、解決できない場合は undefined にする
   * (その場合はコンテンツカードまでのスクロールにフォールバックする)。
   */
  testId?: string;
}

/**
 * バリデーションエラー 1 件。
 *
 * `message` は従来のバリデーション文言と完全に同一 (後方互換のため)。
 */
export interface ValidationIssue {
  message: string;
  target: ValidationTarget;
}

/**
 * `ValidationIssue[]` から先頭の message を取り出す (従来 API 互換用)。
 *
 * `validateContent()` / `validate()` が `string | null` を返す契約を維持するために使う。
 */
export function firstIssueMessage(issues: ValidationIssue[]): string | null {
  return issues.length > 0 ? (issues[0] as ValidationIssue).message : null;
}

/**
 * 指定コンテンツ index に属する issue だけを抽出する。
 * コンテンツカードのエラーバッジ (件数表示) に使う。
 */
export function issuesForContent(
  issues: ValidationIssue[],
  contentIndex: number,
): ValidationIssue[] {
  return issues.filter((i) => i.target.contentIndex === contentIndex);
}

/**
 * 指定コンテンツ・接続線に属する issue だけを抽出する。
 * 接続線タブのエラーマーク (⚠) に使う。
 */
export function issuesForLine(
  issues: ValidationIssue[],
  contentIndex: number,
  lineIndex: number,
): ValidationIssue[] {
  return issues.filter(
    (i) => i.target.contentIndex === contentIndex && i.target.lineIndex === lineIndex,
  );
}

/**
 * 指定コンテンツ・接続線・方面に属する issue だけを抽出する。
 * 方面タブのエラーマーク (⚠) に使う。
 */
export function issuesForDirection(
  issues: ValidationIssue[],
  contentIndex: number,
  lineIndex: number,
  directionIndex: number,
): ValidationIssue[] {
  return issues.filter(
    (i) =>
      i.target.contentIndex === contentIndex &&
      i.target.lineIndex === lineIndex &&
      i.target.directionIndex === directionIndex,
  );
}

/**
 * 「この入力欄にエラーがあるか」を `data-testid` で判定する。
 * 入力欄に `.is-invalid` (赤枠) を付けるかどうかの判断に使う。
 */
export function hasIssueForTestId(issues: ValidationIssue[], testId: string): boolean {
  return issues.some((i) => i.target.testId === testId);
}

/**
 * 「隠れている入力欄を開いて見せる」ための指示 (reveal 指示)。
 *
 * エラーサマリの項目をクリックした / 保存に失敗した際に、親フォーム
 * (`BroadcastPointForm`) が生成して子コンポーネントへ props で流す。
 * 各コンポーネントは `useEffect` で自分の開閉 state を指示に合わせる:
 *
 * - `BroadcastContentsEditor` → アコーディオンを `contentIndex` に開く
 * - `ConnectingLineDelayByStationFields` → 接続線タブを `lineIndex` に切替
 * - `ConnectingDirectionsSection` → 方面タブを `directionIndex` に切替
 *
 * ## なぜ `nonce` が必要か
 *
 * 「同じ項目をもう一度開き直す」(例: ユーザーがタブを手で切り替えたあと、
 * サマリの同じ行を再クリックする) 場合、`contentIndex` などの値は前回と同じ
 * なので `useEffect` の依存配列が変化せず **再実行されない**。
 * クリックごとに単調増加する `nonce` を含めることで、値が同じでも
 * 「もう一度 reveal してほしい」という指示を確実に伝えられる。
 */
export interface RevealTarget {
  contentIndex?: number;
  lineIndex?: number;
  directionIndex?: number;
  /** クリック / 保存失敗ごとに単調増加する識別子 (同一項目の再 reveal 用) */
  nonce: number;
}

/**
 * `ValidationTarget` を `RevealTarget` に変換する。
 *
 * `testId` / `patternIndex` などは「開く」操作には不要なので落とし、
 * 折りたたみ・タブの制御に必要な index 群だけを取り出す。
 */
export function toRevealTarget(target: ValidationTarget, nonce: number): RevealTarget {
  return {
    contentIndex: target.contentIndex,
    lineIndex: target.lineIndex,
    directionIndex: target.directionIndex,
    nonce,
  };
}
